import { motion } from "framer-motion";
import { type FC, useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { formatAbbrevNumber, formatUsd } from "../../../../utils/currency";
import { SIGNAL_HEX } from "../../../common/colorTokens";
import { ProviderIcon } from "../../../common/ProviderIcon";
import { type BlackboardInstance } from "../../blackboardModels";
import { resolveModel } from "../../modelRegistry";
import {
  CornerBrackets,
  PillButton,
  RetroError,
  SectionLabel,
} from "../../retro";

interface CostTabProps {
  instance: BlackboardInstance;
  burnRateUsdPerHr: number | null;
}

type Window = "1h" | "24h" | "7d" | "all";

const WINDOW_OPTIONS: { label: string; value: Window }[] = [
  { label: "1h", value: "1h" },
  { label: "24h", value: "24h" },
  { label: "7d", value: "7d" },
  { label: "All", value: "all" },
];

const getIdleBurnRateLabel = (status: BlackboardInstance["status"]) => {
  if (status === "running") {
    return "—";
  }
  return "IDLE";
};

const getMetricToneColor = (tone: "primary" | "amber" | "default") => {
  if (tone === "primary") {
    return SIGNAL_HEX.healthy;
  }
  if (tone === "amber") {
    return SIGNAL_HEX.warning;
  }
  return SIGNAL_HEX.muted;
};

const costTotalsSchema = z.object({
  calls: z.number(),
  costUsd: z.number(),
  tokens: z.number(),
});
const costBreakdownSchema = z.object({
  byModel: z.array(
    costTotalsSchema.extend({ model: z.string(), provider: z.string() }),
  ),
  byStage: z.array(costTotalsSchema.extend({ stage: z.string() })),
  total: costTotalsSchema,
  window: z.enum(["1h", "24h", "7d", "all"]),
});
type CostBreakdown = z.infer<typeof costBreakdownSchema>;

export const CostTab: FC<CostTabProps> = ({ instance, burnRateUsdPerHr }) => {
  const [window, setWindow] = useState<Window>("24h");
  const [data, setData] = useState<CostBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await fetch(
        `/user/instances/${instance.id}/cost?window=${window}`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const json = costBreakdownSchema.parse(await resp.json());
      setData(json);
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Failed to load cost");
    } finally {
      setLoading(false);
    }
  }, [instance.id, window]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="flex h-full flex-col">
      {/* Window selector */}
      <div className="flex items-center gap-1.5 border-b border-emerald-400/10 bg-slate-100/60 dark:bg-slate-950/40 px-4 py-2">
        {WINDOW_OPTIONS.map((opt) => (
          <PillButton
            key={opt.value}
            active={opt.value === window}
            onClick={() => setWindow(opt.value)}
          >
            {opt.label}
          </PillButton>
        ))}
        {loading && (
          <span className="ml-auto font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-emerald-700/80 dark:text-emerald-300/60">
            [ SYNCING… ]
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {err && (
          <div className="px-4 py-2">
            <RetroError message={err} />
          </div>
        )}

        {/* Headline metrics */}
        <div className="grid grid-cols-3 gap-2 border-b border-emerald-400/10 bg-slate-100/60 dark:bg-slate-950/40 p-3">
          <MetricCell
            label="TOTAL COST"
            value={formatUsd(data?.total.costUsd ?? 0)}
            tone="primary"
          />
          <MetricCell
            label="BURN RATE"
            value={
              burnRateUsdPerHr === null
                ? getIdleBurnRateLabel(instance.status)
                : `${formatUsd(burnRateUsdPerHr)}/hr`
            }
            tone="amber"
          />
          <MetricCell
            label="CALLS"
            value={formatCount(data?.total.calls ?? 0)}
            secondary={`${formatAbbrevNumber(data?.total.tokens ?? 0, "locale")} tokens`}
          />
        </div>

        {/* Foreground = main act phase (the actor's own thinking).
            Background = runner-owned work: consolidation, cache-warm pings,
            compact-seed generation. These fire on Haiku regardless of the
            actor's main model, and their cost is attributed to the actor
            that triggered them. */}
        {(() => {
          const foreground =
            data?.byStage.filter((r) => r.stage === "arti.chat.v1") ?? [];
          const background =
            data?.byStage.filter((r) => r.stage !== "arti.chat.v1") ?? [];
          const bgCost = background.reduce((s, r) => s + r.costUsd, 0);
          const bgTokens = background.reduce((s, r) => s + r.tokens, 0);
          const bgCalls = background.reduce((s, r) => s + r.calls, 0);
          return (
            <>
              <Section
                title="Foreground · act phase"
                empty={foreground.length === 0}
                accent="emerald"
              >
                {foreground.map((row) => (
                  <BreakdownRow
                    key={row.stage}
                    primary={prettyStage(row.stage)}
                    cost={row.costUsd}
                    tokens={row.tokens}
                    calls={row.calls}
                    share={row.costUsd / (data?.total.costUsd || 1)}
                    color={stageColor(row.stage)}
                  />
                ))}
              </Section>
              <Section
                title="Background runner"
                empty={background.length === 0}
                accent="sky"
                subtitle={
                  background.length > 0
                    ? `${formatUsd(bgCost)} · ${formatAbbrevNumber(bgTokens, "locale")} tok · ${formatCount(bgCalls)} calls`
                    : undefined
                }
              >
                {background.map((row) => (
                  <BreakdownRow
                    key={row.stage}
                    primary={prettyStage(row.stage)}
                    cost={row.costUsd}
                    tokens={row.tokens}
                    calls={row.calls}
                    share={row.costUsd / (data?.total.costUsd || 1)}
                    color={stageColor(row.stage)}
                  />
                ))}
              </Section>
            </>
          );
        })()}

        {/* By model */}
        <Section
          title="By model"
          empty={data?.byModel.length === 0}
          accent="violet"
        >
          {data?.byModel.map((row) => (
            <BreakdownRow
              key={`${row.provider}|${row.model}`}
              primary={prettyModel(row.model)}
              icon={
                <ProviderIcon provider={row.provider} className="h-3 w-3" />
              }
              cost={row.costUsd}
              tokens={row.tokens}
              calls={row.calls}
              share={row.costUsd / (data.total.costUsd || 1)}
              color="bg-violet-500/70"
            />
          ))}
        </Section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const MetricCell: FC<{
  label: string;
  value: string;
  secondary?: string;
  tone?: "primary" | "amber" | "default";
}> = ({ label, value, secondary, tone = "default" }) => {
  const toneColor = getMetricToneColor(tone);
  return (
    <div
      className="relative border bg-white/60 px-3 py-2 dark:bg-slate-900/60"
      style={{ borderColor: `${toneColor}33` }}
    >
      <CornerBrackets color={toneColor} />
      <span className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500">
        {label}
      </span>
      <div
        className="mt-0.5 font-mono text-sm font-semibold tabular-nums"
        style={{ color: toneColor }}
      >
        {value}
      </div>
      {secondary && (
        <span className="font-mono text-[10px] tracking-tight text-slate-500">
          {secondary}
        </span>
      )}
    </div>
  );
};

const Section: FC<{
  title: string;
  subtitle?: string;
  empty?: boolean;
  children: React.ReactNode;
  accent?: "emerald" | "violet" | "sky" | "amber";
}> = ({ title, subtitle, empty, children, accent = "emerald" }) => (
  <div className="flex flex-col gap-2 px-4 py-3">
    <SectionLabel
      label={title}
      accent={accent}
      meta={
        subtitle ? <span className="tabular-nums">{subtitle}</span> : undefined
      }
    />
    {empty ? (
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-600">
        [ NO ACTIVITY ]
      </span>
    ) : (
      <div className="flex flex-col gap-2">{children}</div>
    )}
  </div>
);

const BreakdownRow: FC<{
  primary: string;
  icon?: React.ReactNode;
  cost: number;
  tokens: number;
  calls: number;
  share: number;
  color: string;
}> = ({ primary, icon, cost, tokens, calls, share, color }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-2">
      {icon}
      <span className="flex-1 font-mono text-[11px] text-slate-800 dark:text-slate-200">
        {primary}
      </span>
      <span className="font-mono text-[11px] font-bold tabular-nums text-slate-50">
        {formatUsd(cost)}
      </span>
    </div>
    <div className="h-1 w-full overflow-hidden bg-slate-200 dark:bg-slate-800/80">
      <motion.div
        className={`h-full ${color}`}
        initial={{ width: 0 }}
        animate={{ width: `${Math.round(share * 100)}%` }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
    </div>
    <div className="flex gap-3 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
      <span className="tabular-nums">
        {formatAbbrevNumber(tokens, "locale")} tok
      </span>
      <span className="tabular-nums">
        {formatCount(calls)} call{calls === 1 ? "" : "s"}
      </span>
      <span className="ml-auto tabular-nums">{(share * 100).toFixed(1)}%</span>
    </div>
  </div>
);

// ---------------------------------------------------------------------------

function prettyStage(stage: string): string {
  // Strip the shared `arti.chat.v1` prefix; keep sub-stage suffix if any.
  if (stage === "arti.chat.v1") {
    return "act phase";
  }
  if (stage.startsWith("arti.chat.v1.")) {
    return stage.slice("arti.chat.v1.".length).replace(/_/g, " ");
  }
  return stage;
}

function prettyModel(modelRef: string): string {
  return resolveModel(modelRef).label;
}

function stageColor(stage: string): string {
  switch (stage) {
    case "arti.chat.v1": {
      return "bg-violet-500/70";
    }
    case "arti.chat.v1.consolidation": {
      return "bg-sky-400/60";
    }
    case "arti.chat.v1.compact_seed": {
      return "bg-amber-400";
    }
    default: {
      return "bg-slate-400";
    }
  }
}

function formatCount(n: number): string {
  return n.toLocaleString();
}
